import { useEffect, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import Swal, {SweetAlertIcon} from 'sweetalert2';
import moment from 'moment';

// Apis
import {getDealsApi} from '../../api/deals';
import {getCategoriesApi} from '../../api/categories';
import {getCitiesApi} from '../../api/cities';

// Components
import DealsList from './List';
import FilterSideBar from './List/FilterSideBar';
import SearchBar from '../Common/Search/Bar';
import UserLayout from '../Common/Layout/User';
import {saveFilterOptions} from '../Common/Search/Filter/Save';

// Context
import { useAuthStore } from '../../context/Auth';
import { useTranslationStore } from '../../context/Translation';

// Enums
import { CategoryTypes } from '../../enums/Categories';
import { DealTypes, ListLimit } from '../../enums/Deals';
import { Locations } from '../../enums/Filter';
import { LanguageResource } from '../../enums/Languages';
import { SwalIcons, SwalTitle } from '../../enums/Swal';
import { YesNo } from '../../enums/YesNo';

// Interfaces
import { CategoryInterface } from '../../interfaces/Category';
import { DealInterface } from '../../interfaces/Deal';
import { LocationInterface } from '../../interfaces/Location';

// Utils
import DateUtil from '../../utils/DateUtil';
import JSUtil from '../../utils/JSUtil';

interface PropsInterface {
  navigate: NavigateFunction,
  title: string
}

interface GetDealsApiCallInterface {
  latitude: number | null;
  longitude: number | null;
  currentLocation?: string | null;
  highlighted?: string | null;
  limit?: number;
}

const DealsPage = (props: PropsInterface) => {
  const [isLoading, setIsLoading] = useState(true),
    [loadingLocations, setLoadingLocations] = useState<boolean>(true),
    [loadingCategories, setLoadingCategories] = useState<boolean>(true),
    [deals, setDeals] = useState<DealInterface[]>([]),
    [categories, setCategories] = useState<CategoryInterface[]>([]),
    [filteredCategories, setFilteredCategories] = useState<string[]>([]),
    [locations, setLocations] = useState<LocationInterface[]>([]),
    [amount, setAmount] = useState<number>(0),
    [search, setSearch] = useState<string>(''),
    [location, setLocation] = useState<LocationInterface>(
      {} as LocationInterface,
    ),
    [fromDate, setFromDate] = useState<string>(DateUtil.formatInputDate(DateUtil.now())),
    [toDate, setToDate] = useState<string>(DateUtil.formatInputDate(DateUtil.nowMonthsFurther(2))),
    [dateFiltered, setDateFiltered] = useState<boolean>(false),
    [distance, setDistance] = useState<number>(15),
    [dealType, setDealType] = useState<string>(''),
    [sortBy, setSortBy] = useState<string>(''),
    [limit, setLimit] = useState<number>(ListLimit),
    [authState, authDispatch] = useAuthStore(),
    [t] = useTranslationStore();

  let isFiltering =
    !JSUtil.isEmpty(search) ||
    !JSUtil.isEmpty(dealType) ||
    !JSUtil.isEmpty(location) ||
    dateFiltered;
    
  useEffect(() => {
    if (!JSUtil.isEmpty(search) || location._id === '99999') {
      const delaySearch = setTimeout(() => {
        loadDeals();
      }, 1000);
  
      return () => clearTimeout(delaySearch);
    } else {
      loadDeals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filteredCategories, location, distance, dealType, dateFiltered, toDate, fromDate, limit, sortBy, authState.loggedIn, authState.token]);

  useEffect(() => {
    loadLocations();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDeals = async() => {
    setIsLoading(true);
    await navigator.geolocation.getCurrentPosition(
      position => {
        if (location.name === t('location_' + Locations.Current)) {
          getDealsApiCall({
            latitude: position?.coords?.latitude,
            longitude: position?.coords?.longitude,
            currentLocation:
              position?.coords?.latitude + ',' + position?.coords?.longitude,
            highlighted: null,
            limit,
          })
        } else {
          getDealsApiCall({
            latitude: null,
            longitude: null,
            currentLocation:
              position?.coords?.latitude + ',' + position?.coords?.longitude,
            highlighted: null,
            limit,
          });
        }
      },
      err => {
        getDealsApiCall({
        latitude: null,
        longitude: null,
        highlighted: null,
        limit,
      })
    }
    );
  };

  const getDealsApiCall = ({
    latitude,
    longitude,
    currentLocation,
    highlighted,
    limit,
  }: GetDealsApiCallInterface) => {
    let locationQuery: string | LocationInterface = location;
    if (!JSUtil.isEmpty(latitude) && !JSUtil.isEmpty(longitude)) {
      locationQuery = latitude + ',' + longitude;
    } else if (
      !JSUtil.isEmpty(location.latitude) &&
      !JSUtil.isEmpty(location.longitude)
    ) {
      locationQuery = location.latitude + ',' + location.longitude;
    } else if (!JSUtil.isEmpty(location.name)) {
      locationQuery = location.name as string;
    }

    getDealsApi(
      {
        token: authState.token,
        search,
        location: locationQuery,
        currentLocation,
        distance,
        category: filteredCategories.join(','),
        online: dealType
          ? dealType === DealTypes.Online
            ? YesNo.Yes
            : YesNo.No
          : null,
        fromDate: fromDate ? moment(fromDate).unix() * 1000 : undefined,
        toDate: toDate ? moment(toDate).unix() * 1000 : undefined,
        highlighted,
        sortBy,
        limit,
      },
      (err: any, res: {amount: number; result: DealInterface[]}) => {
        setIsLoading(false);
        if (err) {
          Swal.fire({
            icon: SwalIcons.Error as SweetAlertIcon,
            title: t(SwalTitle.Error),
            text: err,
          });
        } else {
          setDeals(res.result);
          setAmount(res.amount);
          saveFilterOptions(
            authState.token,
            {
              search,
              categories: filteredCategories,
              dealType,
              location: location.name,
              distance,
              fromDate,
              toDate
            },
            isFiltering,
            res.amount,
            CategoryTypes.Deals,
            err => {
              if (err) {
                Swal.fire({
                  icon: SwalIcons.Error as SweetAlertIcon,
                  title: t(SwalTitle.Error),
                  text: err,
                });
              }
            },
          );
        }
      },
      authDispatch,
    );
  }

  const loadLocations = () => {
    getCitiesApi(
      {
        token: authState.token,
        filter: YesNo.Yes,
      },
      (err: any, res: LocationInterface[]) => {
        if (err) {
          Swal.fire({
            icon: SwalIcons.Error as SweetAlertIcon,
            title: t(SwalTitle.Error),
            text: err,
          });
        } else {
          let result: LocationInterface[] = [
            {_id: '99998', name: t(LanguageResource.Common + ':location_' + Locations.Current)},
          ];
          res.map(location => result.push(location));
          setLocations(result);
        }
        setLoadingLocations(false);
      },
    );
  };

  const loadCategories = () => {
    getCategoriesApi(
      {
        token: authState.token,
        type: CategoryTypes.Deals,
      },
      (err: any, res: CategoryInterface[]) => {
        if (err) {
          Swal.fire({
            icon: SwalIcons.Error as SweetAlertIcon,
            title: t(SwalTitle.Error),
            text: err,
          });
        } else {
          setCategories(res);
        }
        setLoadingCategories(false);
      },
    );
  };

  const onPressCategory = (id: string) => {
    let newFilteredCategories = [...filteredCategories];
    if (newFilteredCategories.includes(id)) {
      const index = newFilteredCategories.indexOf(id);
      if (index > -1) {
        // only splice array when item is found
        newFilteredCategories.splice(index, 1); // 2nd parameter means remove one item only
      }
    } else {
      newFilteredCategories.push(id);
    }
    setFilteredCategories(newFilteredCategories);
  };

  const cleanSearch = () => {
    setSortBy('');
    setLimit(ListLimit);
    setAmount(0);
    setLocation({} as LocationInterface);
    setDistance(15);
    setFilteredCategories([]);
    setDealType('');
    setFromDate(DateUtil.formatInputDate(DateUtil.now()));
    setToDate(DateUtil.formatInputDate(DateUtil.nowMonthsFurther(2)));
    setDateFiltered(false);
  };

  return (
    <UserLayout 
      navigate={props.navigate} 
      title={t(LanguageResource.Common + ':' + props.title)}
      isLoading={isLoading || loadingCategories || loadingLocations}
      sideBarComponent={
        <FilterSideBar
          location={location}
          setLocation={setLocation}
          distance={distance}
          setDistance={setDistance}
          dealType={dealType}
          setDealType={setDealType}
          categories={categories}
          filteredCategories={filteredCategories}
          onPressCategory={onPressCategory}
          locations={locations}
          setFromDate={setFromDate}
          fromDate={fromDate}
          setDateFiltered={setDateFiltered}
          dateFiltered={dateFiltered}
          setToDate={setToDate}
          toDate={toDate}
          sortBy={sortBy}
          setSortBy={setSortBy}
          cleanSearch={cleanSearch}
          filterActive={
            location ||
            distance ||
            dealType ||
            search
            ? true : false
          }
          amount={amount}
        />
      }
      >
      <SearchBar
        search={search}
        setSearch={setSearch}
      />
      <DealsList 
        deals={deals}
        amount={amount}
        limit={limit}
        setLimit={setLimit}
        navigate={props.navigate} 
      />
    </UserLayout>
  );
}

export default DealsPage;